import { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { useSelectedDealership } from "../../hooks/useLocalStorage";
import { showErrorMessage } from "../../shared/Services/toasterService";
import { useLocalization } from "../../context/LocalizationContext";
import { formatPhoneNumber } from "../../shared/helpers/formUtils";
import { format } from "date-fns";
import getUserRoleFromJWT from "../../shared/helpers/getUserRoleFromJWT";
import { UserRole } from "../../shared/Enums/UserRole";

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#f4f4f4",
      color: "#333",
      borderRight: "1px solid #ccc",
      fontSize: "16px",
      fontWeight: 600,
      justifyContent: "flex-start",
    },
  },
  cells: {
    style: {
      borderRight: "1px solid #eee",
      fontSize: "13px",
      justifyContent: "flex-start",
    },
  },
  rows: {
    style: {
      minHeight: "45px",
    },
  },
};

const Customer = () => {
  const { t: localization } = useLocalization();
  const [errors, setErrors] = useState({
    customerName: "",
  });
  const [allData, setAllData] = useState([]);
  const [gridSearch, setGridSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const navigate = useNavigate();
  const [selectedDealershipId] = useSelectedDealership();
  const [filters, setFilters] = useState({
    customerName: "",
    fromDate: null,
    toDate: null,
  });

  const fetchCustomers = async (applyFilters = false) => {
    try {
      let payload = {
        dealershipId: selectedDealershipId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      if (applyFilters) {
        let fromDateFormatted = null;
        if (filters.fromDate) {
          const date = new Date(filters.fromDate);
          date.setHours(0, 0, 0, 0);
          fromDateFormatted = date.toISOString();
        }
        let toDateFormatted = null;
        if (filters.toDate) {
          const date = new Date(filters.toDate);
          date.setHours(23, 59, 59, 999);
          toDateFormatted = date.toISOString();
        }
        payload = {
          ...payload,
          customerName: filters.customerName || null,
          startDate: fromDateFormatted,
          endDate: toDateFormatted,
        };
      }
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_SERVICE_INFO,
        payload,
      );
      const userData = response.data.map((item, index) => ({
        ...item,
        id: index + 1,
        dateOfService: item.dateOfService
          ? format(new Date(item.dateOfService), "MM-dd-yyyy hh:mm a")
          : "",
      }));
      setAllData(userData);
    } catch (error) {
      showErrorMessage(error);
    }
  };

  useEffect(() => {
    fetchCustomers(false);
  }, []);

  const validateInputs = () => {
    let isValid = true;
    const newErrors = { customerName: "" };
    if (
      filters.customerName &&
      !/^(?=.*[A-Za-z])[A-Za-z]+(?:[ \-'\.][A-Za-z]+)*$/.test(
        filters.customerName,
      )
    ) {
      newErrors.customerName = localization("customer_name_invalid");
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const applyFilters = async () => {
    if (!validateInputs()) return;
    if (
      (filters.fromDate && !filters.toDate) ||
      (!filters.fromDate && filters.toDate)
    ) {
      showErrorMessage("Please select both from and to dates");
      return;
    }
    if (filters.fromDate && filters.toDate) {
      const from = new Date(filters.fromDate);
      const to = new Date(filters.toDate);
      if (to < from) {
        showErrorMessage("To date must be after from date");
        return;
      }
    }

    await fetchCustomers(true);
  };

  const clearFilters = async () => {
    setFilters({
      customerName: "",
      fromDate: null,
      toDate: null,
    });
    setGridSearch("");
    await fetchCustomers(false);
  };
  const handleHistoryClick = (row) => {
    navigate("/customer-history", {
      state: { 
        customerId: row.customerId, 
        customerName: row.fullName,
        vehicleId: row.vehicleId,
        vehicleName: row.modelName,
        vin: row.vin
      },
    });
  };

  const handleViewClick = async (row) => {
    setShowModal(true);
    setCustomerDetails(null);
    setVehicleDetails(null);
    try {
      const customerIdPayload = {
        id: row.customerId,
      };
      const vehicleIdPayload = {
        id: row.vehicleId,
      };
      const customerRes = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_BY_ID,
        customerIdPayload,
      );
      const vehicleRes = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID,
        vehicleIdPayload,
      );
      const decryptedCustomer = customerRes.data;
      const decryptedVehicle = vehicleRes.data;
      setCustomerDetails(decryptedCustomer);
      setVehicleDetails(decryptedVehicle);
    } catch (error) {
      showErrorMessage(error);
    }
  };

  const columns = [
    {
      name: localization("customer"),
      selector: (row) => row.fullName,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("vehicle"),
      selector: (row) => row.modelName,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("vin"),
      selector: (row) => row.vin || localization("not_available"),
      sortable: true,
      wrap: true,
    },
    {
      name: localization("dealership"),
      selector: (row) => row.dealerShip,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("last_service_date"),
      selector: (row) => row.dateOfService,
      sortable: true,
      width: "190px",
    },
    {
      name: (
        <div style={{ width: "100%", textAlign: "left" }}>
          {localization("actions")}
        </div>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-start align-items-center gap-2">
          <button
            onClick={() => handleViewClick(row)}
            className="btn btn-blue btn-sm"
            title={localization("view_customer")}
          >
            <i className="fas fa-eye"></i>
          </button>
          <button
            onClick={() => handleHistoryClick(row)}
            className="btn btn-golden-yellow btn-sm"
            title={localization("view_history")}
          >
            <i className="fas fa-history"></i>
          </button>
        </div>
      ),
      style: { justifyContent: "flex-start" },
      ignoreRowClick: true,
      allowOverflow: true,
      width: "100px",
    },
  ];

  const filteredData = allData.filter((item) => {
    const matchesSearch =
      gridSearch.trim() === "" ||
      Object.values(item).some(
        (val) =>
          val &&
          val.toString().toLowerCase().includes(gridSearch.toLowerCase()),
      );
    return matchesSearch;
  });

  return (
    <div className="container-fluid content-wrapper select-vehicle-content">
      <div className="content form-card">
        <div className="container-fluid p-0 mb-3">
          <div className="page-title">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-md-6">
                  <h1>{localization("customers")}</h1>
                </div>
                <div className="col-md-6 d-flex justify-content-end">
                  <div className="d-flex flex-wrap justify-content-end">
                    <input
                      type="text"
                      placeholder={localization("search")}
                      className="form-control me-2"
                      style={{ width: "50%", paddingRight: "2.5rem" }}
                      value={gridSearch}
                      onChange={(e) => setGridSearch(e.target.value)}
                    />
                    <button
                      className="btn btn-success"
                      onClick={() => {
                        navigate("/second-step", { state: { reset: true } });
                      }}
                      style={{ fontSize: "1.1rem", padding: "6px 16px" }}
                    >
                      {localization("start_service")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-2">
              <input
                type="text"
                placeholder={localization("customer_name")}
                value={filters.customerName}
                onChange={(e) =>
                  setFilters({ ...filters, customerName: e.target.value })
                }
                className="form-control"
                style={{ width: "100%" }}
              />
              {errors.customerName && (
                <div className="text-danger small">{errors.customerName}</div>
              )}
            </div>
            <div className="col-md-2">
              <DatePicker
                selected={filters.fromDate}
                onChange={(date) => setFilters({ ...filters, fromDate: date })}
                className="form-control"
                placeholderText={localization("from_date")}
                dateFormat="yyyy-MM-dd"
                maxDate={filters.toDate || new Date()}
              />
            </div>
            <div className="col-md-2">
              <DatePicker
                selected={filters.toDate}
                onChange={(date) => setFilters({ ...filters, toDate: date })}
                className="form-control"
                placeholderText={localization("to_date")}
                minDate={filters.fromDate}
                maxDate={new Date()}
                dateFormat="yyyy-MM-dd"
              />
            </div>
            <div className="col-md-3 d-flex gap-2">
              <button
                className="btn btn-blue d-flex align-items-center justify-content-center gap-2"
                onClick={applyFilters}
                title={localization("apply_filters")}
              >
                <i className="fas fa-filter"></i>
              </button>
              <button
                className="btn btn-danger d-flex align-items-center justify-content-center gap-2"
                onClick={clearFilters}
                title={localization("clear_filters")}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
        <div>
          <DataTable
            columns={columns}
            data={filteredData.length > 0 ? filteredData : []}
            pagination={filteredData.length > 0}
            striped
            highlightOnHover
            pointerOnHover
            noDataComponent={localization("no_record_found")}
            responsive
            customStyles={customStyles}
            noHeader
          />
          <button
            className="btn btn-blue mt-4"
            onClick={() => {
              const userRole = getUserRoleFromJWT();
              navigate(
                userRole === UserRole.DEALER_ADMIN
                  ? "/admin-dashboard"
                  : "/service-desk-dashboard",
              );
            }}
          >
            {localization("back")}
          </button>
        </div>
      </div>
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {localization("customer_vehicle_details")}
                </h5>
              </div>
              <div
                className="modal-body"
                style={{ maxHeight: "60vh", overflowY: "auto" }}
              >
                {!customerDetails || !vehicleDetails ? (
                  <div>{localization("loading_details")}</div>
                ) : (
                  <>
                    <h6 className="mb-3">
                      <strong>{localization("customer_info")}</strong>
                    </h6>
                    <table className="table table-bordered mb-4">
                      <tbody>
                        <tr>
                          <th style={{ width: "30%" }}>
                            {localization("name")}
                          </th>
                          <td>
                            {customerDetails.fullName ||
                              localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("email")}</th>
                          <td>
                            {customerDetails.email ||
                              localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("phone")}</th>
                          <td>
                            {customerDetails.phoneNumber
                              ? formatPhoneNumber(customerDetails.phoneNumber)
                              : localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("address")}</th>
                          <td>
                            {customerDetails.address1 ||
                              localization("not_available")}
                            {customerDetails.cityOrLocality
                              ? `, ${customerDetails.cityOrLocality}`
                              : ""}
                            {customerDetails.postalCodeOrZip
                              ? `, ${customerDetails.postalCodeOrZip}`
                              : ""}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <h6 className="mb-3">
                      <strong>{localization("vehicle_info")}</strong>
                    </h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <th style={{ width: "30%" }}>
                            {localization("year")}
                          </th>
                          <td>
                            {vehicleDetails.year ||
                              localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("make")}</th>
                          <td>
                            {vehicleDetails.makeName ||
                              localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("model")}</th>
                          <td>
                            {vehicleDetails.modelName ||
                              localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("vin")}</th>
                          <td>
                            {vehicleDetails.vin ||
                              localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("body")}</th>
                          <td>
                            {vehicleDetails.body ||
                              localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("mileage")}</th>
                          <td>
                            {vehicleDetails.Mileage ||
                              localization("not_available")}
                          </td>
                        </tr>
                        <tr>
                          <th>{localization("engine")}</th>
                          <td>
                            {vehicleDetails.engine ||
                              localization("not_available")}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  {localization("close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customer;
